import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { PageLoader } from '../../../components';
import { useSchedulingProfile } from './hooks';
import { BookingPageTab } from './tabs/BookingPageTab';
import { HoursTab } from './tabs/HoursTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { ResourcesTab } from './tabs/ResourcesTab';
import { ServicesTab } from './tabs/ServicesTab';
import { SettingsTab } from './tabs/SettingsTab';
import { SyncTab } from './tabs/SyncTab';

const TABS = [
  'resources',
  'services',
  'hours',
  'bookingPage',
  'notifications',
  'sync',
  'settings',
] as const;
type TabId = (typeof TABS)[number];

export const SchedulingCalendarDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: profile, isLoading, isError } = useSchedulingProfile(id ?? '');
  const [tab, setTab] = useState<TabId>('resources');

  if (isLoading) return <PageLoader show />;

  if (isError || !profile) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-gray-600 dark:text-gray-400">{t('schedulingAdmin.detail.notFound')}</p>
        <Button variant="secondary" onClick={() => navigate('/calendars')}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          title={t('common.back')}
          onClick={() => navigate('/calendars')}
        >
          <span className="material-icons">arrow_back</span>
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{profile.name}</h1>
        <Badge variant="info" size="sm">
          {t('schedulingAdmin.list.typeBadge')}
        </Badge>
        {!profile.is_active && (
          <Badge variant="warning" size="sm">
            {t('common.inactive')}
          </Badge>
        )}
        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          onClick={() => navigate(`/appointments/${profile.id}`)}
        >
          {t('schedulingAdmin.detail.toAppointments')}
        </Button>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6 overflow-x-auto">
          {TABS.map(tabId => (
            <button
              key={tabId}
              type="button"
              onClick={() => setTab(tabId)}
              className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                tab === tabId
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t(`schedulingAdmin.tabs.${tabId}`)}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'resources' && <ResourcesTab profile={profile} />}
      {tab === 'services' && <ServicesTab profile={profile} />}
      {tab === 'hours' && <HoursTab profile={profile} />}
      {tab === 'bookingPage' && <BookingPageTab profile={profile} />}
      {tab === 'notifications' && <NotificationsTab profile={profile} />}
      {tab === 'sync' && <SyncTab profile={profile} />}
      {tab === 'settings' && <SettingsTab profile={profile} />}
    </div>
  );
};
